import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Download, Pencil, Trash2, ChevronLeft, ChevronRight, X, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { TableState } from '@/components/DataState';

function formatSender(sender, senderName) {
  if (!sender) return 'N/A';
  if (sender.startsWith('upload:')) {
    const parts = sender.split(':');
    const name = parts[1] || '';
    const email = parts[2] || '';
    return `Upload — ${name}${email ? ` (${email})` : ''}`;
  }
  if (sender === 'manual_upload') return 'Upload';
  const phone = sender.replace('whatsapp:', '');
  if (senderName) return `${senderName} ${phone}`;
  return phone;
}

export default function ReceiptsPage() {
  const [receipts, setReceipts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [editReceipt, setEditReceipt] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, sort_dir: sortDir });
      if (search) params.set('search', search);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await api.get(`/receipts?${params}`);
      setReceipts(data.receipts);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { void 0; }
    setLoading(false);
  }, [page, search, dateFrom, dateTo, sortDir]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const response = await api.get(`/receipts-export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `zipai_receipts_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleDelete = async (receiptId) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      await api.delete(`/receipts/${receiptId}`);
      toast.success('Receipt deleted');
      fetchReceipts();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleEdit = (receipt) => {
    setEditReceipt(receipt);
    setEditForm({
      invoice_date: receipt.invoice_date || '',
      invoice_number: receipt.invoice_number || '',
      company: receipt.company || '',
      items: receipt.items || '',
      payment_method: receipt.payment_method || '',
      total: receipt.total || '',
    });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/receipts/${editReceipt.receipt_id}`, editForm);
      toast.success('Receipt updated');
      setEditReceipt(null);
      fetchReceipts();
    } catch {
      toast.error('Update failed');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchReceipts();
  };

  const handlePreview = async (receipt) => {
    setPreviewReceipt(receipt);
    if (receipt.file_id) {
      try {
        const response = await api.get(`/files/${receipt.file_id}/preview`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        setPreviewUrl(window.URL.createObjectURL(blob));
      } catch {
        setPreviewUrl(null);
      }
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewReceipt(null);
  };

  const toggleSort = () => {
    setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Receipts</h1>
          <p className="text-muted-foreground text-sm mt-1">{total} receipts processed</p>
        </div>
        <Button onClick={handleExport} variant="outline" className="rounded-[4px] shrink-0" data-testid="export-btn">
          <Download className="w-4 h-4 mr-2" /> Export to Excel
        </Button>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-wrap gap-3" data-testid="receipts-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, invoice, items..."
            className="pl-10 h-10 rounded-[4px]"
            data-testid="receipts-search-input"
          />
        </div>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="w-[160px] h-10 rounded-[4px]"
          data-testid="receipts-date-from"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="w-[160px] h-10 rounded-[4px]"
          data-testid="receipts-date-to"
        />
        <Button type="submit" variant="outline" className="h-10 rounded-[4px]" data-testid="receipts-filter-btn">
          Filter
        </Button>
        {(search || dateFrom || dateTo) && (
          <Button type="button" variant="ghost" className="h-10" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setPage(1); }} data-testid="receipts-clear-filter">
            <X className="w-4 h-4" />
          </Button>
        )}
      </form>

      {/* Table */}
      <div className="border border-border rounded-md overflow-hidden" data-testid="receipts-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold cursor-pointer select-none hover:text-foreground transition-colors" onClick={toggleSort} data-testid="sort-date-col">
                  Date {sortDir === 'desc' ? '↓' : '↑'}
                </th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Invoice #</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Company</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden lg:table-cell">Items</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">Payment</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold hidden lg:table-cell">Sender</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <TableState loading={loading} empty={receipts.length === 0} colSpan={8} emptyText="No receipts found">
                {receipts.map((r) => (
                  <tr key={r.receipt_id} className="hover:bg-secondary/30 transition-colors" data-testid={`receipt-row-${r.receipt_id}`}>
                    <td className="px-4 py-3 text-sm font-mono">{r.invoice_date || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm font-mono">{r.invoice_number || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm font-medium">{r.company || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell max-w-[350px]">
                      <div className="whitespace-pre-wrap break-words">{r.items || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm hidden md:table-cell">{r.payment_method || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-right">{r.total || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
                      {formatSender(r.sender, r.sender_name)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {r.file_id && (
                          <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => handlePreview(r)} data-testid={`view-receipt-${r.receipt_id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => handleEdit(r)} data-testid={`edit-receipt-${r.receipt_id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-destructive hover:text-destructive" onClick={() => handleDelete(r.receipt_id)} data-testid={`delete-receipt-${r.receipt_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </TableState>
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="receipts-pagination">
            <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="w-7 h-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="receipts-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="w-7 h-7" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="receipts-next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editReceipt} onOpenChange={(open) => !open && setEditReceipt(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-receipt-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Edit Receipt</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {['invoice_date', 'invoice_number', 'company', 'items', 'payment_method', 'total'].map((field) => (
              <div key={field} className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground">
                  {field.replace('_', ' ')}
                </Label>
                <Input
                  value={editForm[field] || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                  className="rounded-[4px]"
                  data-testid={`edit-field-${field}`}
                />
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditReceipt(null)} className="rounded-[4px]" data-testid="edit-cancel-btn">Cancel</Button>
              <Button onClick={handleSaveEdit} className="rounded-[4px]" data-testid="edit-save-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={!!previewReceipt} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]" data-testid="receipt-preview-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {previewReceipt?.company || 'Receipt'} — {previewReceipt?.filename || 'File'}
            </DialogTitle>
          </DialogHeader>
          {previewReceipt && previewUrl ? (
            <div className="flex justify-center py-2">
              <img
                src={previewUrl}
                alt="Receipt preview"
                className="max-h-[600px] w-auto object-contain rounded"
              />
            </div>
          ) : previewReceipt && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
